import { useEffect, useRef } from 'react';

import * as S from './FeaturedActivities.styles';
import { SliderDots } from '@/common/ui';
import { Container } from '@/common/ui/containers/Container';
import { useCarousel } from '@/common/app/hooks/useCarousel';
import { IRecommendsItem } from './featuredActivitiesPreview/FeaturedActivitiesPreview.types';
import { FeaturedActivitiesSkeleton } from './FeaturedActivitiesSkeleton/FeaturedActivitiesSkeleton';
import { NewFeaturedActivitiesPreview } from './featuredActivitiesPreview/NewFeaturedActivitiesPreview';
import { useRecommended } from '@/common/app/hooks/useRecommended';
import { useMedia } from '@/common/service/hooks/useMedia';

export type FeaturedActivitiesProps = {
    defaultActivities: IRecommendsItem[];
    title?: string;
    subTitle?: string;
    item_list_name: string;
    item_list_id: string;
    placeholders: {
        [key: string]: string;
    };
    withoutDots?: boolean;
    currentCategoryId?: string;
    noPadding?: boolean;
    noPaddingContainer?: boolean;
    isLoading?: boolean;
    isDirectList?: boolean;
    isWidget?: boolean;
};

export const NewFeaturedActivities = ({
    defaultActivities,
    title = '',
    subTitle,
    item_list_name,
    item_list_id,
    placeholders,
    withoutDots,
    currentCategoryId,
    noPadding,
    noPaddingContainer,
    isLoading,
    isDirectList,
    isWidget,
}: FeaturedActivitiesProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { loading: stateLoading, recommended: stateRecommended } = useRecommended({
        defaultRecommended: defaultActivities,
        currentCategoryId,
    });
    const isDesktop = useMedia('(min-width: 992px)');
    const isWebApp = useMedia('(display-mode: standalone)');

    const loading = isDirectList ? isLoading : stateLoading;
    const recommended = isDirectList ? defaultActivities : stateRecommended;
    const isMoreElements = recommended.length > 1;

    const {
        prevBtnIsActive,
        nextBtnIsActive,
        onClickHandler,
        onScrollHandler,
        DIRECTION,
        withDot,
        dotActive,
    } = useCarousel({
        ref: scrollRef,
        countItems: recommended.length || 4,
        loading,
    });

    const skeletonObj = new Array(4).fill({ id: '' });

    useEffect(() => {
        if (isWidget) {
            const body = document.querySelector('body');
            if (body) {
                body.style.backgroundColor = 'transparent';
                document.documentElement.style.overflow = 'hidden';
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!loading && !recommended.length) {
        return <></>;
    }

    if (!defaultActivities.length && !isDirectList) {
        return <></>;
    }

    return (
        <S.Wrapper noPadding={noPadding} noPaddingContainer={noPaddingContainer}>
            <Container isWidget={isWidget}>
                <S.Block data-test-id="Widget-attraction" className="container">
                    {Boolean(title) && (
                        <S.Header>
                            <S.LeftPartHeader>
                                <S.Title>{title}</S.Title>
                                {subTitle && <S.SubTitle>{subTitle}</S.SubTitle>}
                            </S.LeftPartHeader>
                        </S.Header>
                    )}
                    <S.CarouselWraper>
                        <S.Gallery ref={scrollRef} onScroll={onScrollHandler}>
                            <S.InnerContainer className="carousel-container">
                                {loading ? (
                                    <>
                                        {skeletonObj.map((slide, index) => (
                                            <FeaturedActivitiesSkeleton key={slide.id || index} />
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        {recommended.map((slide, index) => (
                                            <NewFeaturedActivitiesPreview
                                                {...slide}
                                                numberOfItems={recommended.length}
                                                position={index}
                                                key={slide.id}
                                                listName={title}
                                                item_list_id={item_list_id}
                                                item_list_name={item_list_name}
                                                promotion_name={title}
                                                placeholder={
                                                    placeholders?.[slide.id] ||
                                                    slide.placeholder ||
                                                    ''
                                                }
                                                isDesktop={isDesktop}
                                                isWebApp={isWebApp}
                                                isWidget={isWidget}
                                            />
                                        ))}
                                    </>
                                )}
                            </S.InnerContainer>
                        </S.Gallery>
                        {prevBtnIsActive && withDot && isMoreElements && (
                            <S.CenterLeftArrow
                                onClick={() => onClickHandler(DIRECTION.prev)}
                                disabled={false}
                                isWidget={isWidget}
                            />
                        )}
                        {nextBtnIsActive && withDot && isMoreElements && (
                            <S.CenterRightArrow
                                onClick={() => onClickHandler(DIRECTION.next)}
                                disabled={false}
                                isWidget={isWidget}
                            />
                        )}
                    </S.CarouselWraper>
                    {!!withDot && !withoutDots && (
                        <S.DotsSlider>
                            {[...Array(recommended.length).keys()].map((el) => {
                                return (
                                    <SliderDots isActive={el === Math.ceil(dotActive)} key={el} />
                                );
                            })}
                        </S.DotsSlider>
                    )}
                </S.Block>
            </Container>
        </S.Wrapper>
    );
};
